import { KalturaRecordingStatus, KalturaRecordingType } from '@kaltura-ott/ott-client-types/api/types';
import {
  endOfDay,
  FormatKalturaProductPricePpv,
  getDaysBetweenTwoDate,
  getUserRules,
  KuxBookmark,
  KuxProgramType,
  startOfDay,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import type { AppStore } from '../app/app-store';
import { DmsStore } from '../dms/dms-store';
import type { Linear } from '../entities/Linear';
import type { LinearRepository } from '../linearRepository/linear-repository';
import type { RecordRepository } from '../recordRepository';

import { getParentalRatingString } from './parental.service';
import { PlayableAsset } from './PlayableAsset';
import { SeriesRecord, Record } from './Record';

export enum ProgramStateV2 {
  PAST = 'PAST',
  LIVE = 'LIVE',
  FUTURE = 'FUTURE',
}

export enum SpecialLabelCatchUpAirDate {
  TOMORROW = 'tomorrow',
  TODAY = 'today',
  TONIGHT = 'tonight',
}
export interface CatchUpAirDate {
  startDate: number;
  specialLabel?: SpecialLabelCatchUpAirDate;
}

export class Program extends PlayableAsset<KuxProgramType> {
  static moduleName = 'Program';

  public linearIdList: number[] = [];

  private _linear: Linear | undefined;
  private _linears: Linear[] = [];

  @computed get linear(): Linear | undefined {
    if (this._linear) {
      return this._linear;
    }
    const linearRepository = container.resolve<LinearRepository>('linearRepository');

    this._linear = linearRepository.getLinearById(this.data.linearAssetId);
    return this._linear;
  }

  @computed get linears(): Linear[] {
    if (this._linears.length) {
      return this._linears;
    }
    const linearRepository = container.resolve<LinearRepository>('linearRepository');
    this._linears = this.linearIdList.map((id) => linearRepository.getLinearById(id)).filter(linearRepository.isLinear);
    return this._linears;
  }

  @observable mosaicProgram?: Program;

  @observable private static now: number = Date.now();

  init(data?: KuxProgramType) {
    if (data && !this.data) {
      this.data = data;
      this.loadMeta(true);
    }
  }

  async setupRules(): Promise<void> {
    this.rules = await lastValueFrom(getUserRules(this.data.linearAssetId, 1));
  }

  get linearId() {
    return this.linear?.id;
  }

  @computed get isPlayable() {
    const record = this.record;
    if (record && record.data.status === KalturaRecordingStatus.recorded) {
      return true;
    }

    if (this.programState === ProgramStateV2.FUTURE) {
      return false;
    }

    if (this.programState === ProgramStateV2.PAST) {
      // for past program we need to check if catchup enabled
      if (!this.data.enableCatchUp) {
        return false;
      }

      // for past program we need to check catchup expiration
      if (Program.now > this.data.startDate + (this.linear?.data.catchUpBuffer || 0)) {
        return false;
      }
    }

    if (!this.rules) {
      return false;
    }

    return !this.rules.length;
  }

  @computed get isPurchased(): boolean | undefined {
    if (this.isProgramRecord) return true; // KUXEXT-19794
    return this.linear?.isPurchased;
  }

  @computed get isPurchasePending(): boolean | undefined {
    return this.linear?.isPurchasePending;
  }

  @computed get catchupAvailableTime(): number {
    if (!this.linear) {
      return 0;
    }

    if (this.data?.recordData?.viewableUntilDate) {
      return this.data.recordData.viewableUntilDate * 1000;
    }

    if (!this.data.isPopular) {
      const dmsStore = container.resolve<DmsStore>('dmsStore');
      return Number(dmsStore.dms?.playableCatchupBuffer) * 1000 + this.data.startDate;
    }

    return this.linear.data.catchUpBuffer + this.data.startDate;
  }

  @computed get recordAvailableTime(): number | undefined {
    if (!this.record?.data?.viewableUntilDate) {
      return;
    }
    return this.record?.data?.viewableUntilDate * 1000;
  }

  @computed get catchupExpirationTime(): number {
    if (!this.linear) {
      return 0;
    }
    if (!this.data.isPopular) {
      const dmsStore = container.resolve<DmsStore>('dmsStore');
      return Number(dmsStore.dms?.playableCatchupBuffer) * 1000 + this.data.endDate;
    }
    return this.linear.data.catchUpBuffer + this.data.endDate;
  }

  @computed get isNewEpisode(): boolean {
    // only for KalturaRecordingType.series
    if (!this.data.newEpisodeBadgeDays || this.data.recordData?.recordingType === KalturaRecordingType.single)
      return false;
    return Math.abs(getDaysBetweenTwoDate(new Date(), new Date(this.data.startDate))) <= this.data.newEpisodeBadgeDays;
  }

  @computed get catchUpAirDate(): CatchUpAirDate {
    const now = Date.now();
    const { startDate } = this.data;
    const appStore = container.resolve<AppStore>('appStore');
    const primeTime = appStore.primeTime;

    const startOfTommorow = startOfDay(undefined, 1);
    const endOfTommorrow = endOfDay(undefined, 1);

    const startHour = new Date(startDate).getHours();
    if (startDate < now) {
      return { startDate };
    } else if (startDate < startOfTommorow && primeTime && startHour >= primeTime) {
      return { startDate, specialLabel: SpecialLabelCatchUpAirDate.TONIGHT };
    } else if (startDate < startOfTommorow) {
      return { startDate, specialLabel: SpecialLabelCatchUpAirDate.TODAY };
    } else if (startDate < endOfTommorrow) {
      return { startDate, specialLabel: SpecialLabelCatchUpAirDate.TOMORROW };
    }

    return { startDate };
  }
  setPrice(priceValue: FormatKalturaProductPricePpv) {
    if (this.linear) {
      this.linear.price = priceValue;
    }
    this.price = priceValue;
  }

  @computed get isAdult(): boolean | undefined {
    return this.data.adult || this.linear?.data.adult;
  }

  @computed get isRecordable() {
    return this.data?.recordable;
  }

  get recordingExpirationBoundary() {
    return this.data.recordingExpirationBoundary;
  }

  @computed get recordingExpirationInDays() {
    // only for KalturaRecordingType.single and series episodes
    const isSeriesEpisode =
      this.data.recordData?.recordingType === KalturaRecordingType.series &&
      !this.data.recordData?.totalRecordedEpisodes;

    const expiredInDays = this.data.recordData?.viewableUntilDate
      ? getDaysBetweenTwoDate(new Date(this.data.recordData.viewableUntilDate * 1000))
      : undefined;
    if (
      expiredInDays !== undefined &&
      this.recordingExpirationBoundary &&
      expiredInDays <= this.recordingExpirationBoundary &&
      (this.data.recordData?.recordingType === KalturaRecordingType.single || isSeriesEpisode)
    ) {
      return expiredInDays;
    } else {
      return undefined;
    }
  }
  get mediaFileId(): number | undefined {
    this.checkLinear();
    return this.linear?.mediaFileId;
  }

  @computed get isProgramRecord() {
    return this.data?.isRecordProgram;
  }

  @computed public get record(): Record | undefined {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');
    const record = recordRepository.getSingleRecord(this.data?.id);

    if (this.data?.recordData?.recordingId && record) {
      record.data.id = this.data.recordData.recordingId;
      record.data.type = this.data.recordData.recordingType;
      record.data.viewableUntilDate = this.data.recordData.viewableUntilDate;
      record.data.isOngoing =
        this.data.recordData.viewableUntilDate !== undefined &&
        this.data.recordData.viewableUntilDate <= 0 &&
        (this.programState === ProgramStateV2.LIVE || this.programState === ProgramStateV2.PAST);
      record.data.expiredInDays = this.recordingExpirationInDays;

      return record;
    }

    return record;
  }

  get totalRecordedEpisodes() {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');
    const hasRecodring = recordRepository.getSingleRecord(this.data?.id);

    if (hasRecodring) {
      return this.data.recordData?.totalRecordedEpisodes;
    }
  }

  public get seriesRecord(): SeriesRecord | undefined {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');
    return recordRepository.getSeriesRecord(this.data?.seriesId);
  }

  @errorHandling()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public addRecord(seriesId?: string): Promise<{ error: any; message: any } | void> {
    const recordRepository = container.resolve<RecordRepository>('recordRepository');

    return recordRepository.addRecord({ assetId: this.data.id, seriesId });
  }

  @errorHandling()
  public removeRecord(type: KalturaRecordingType) {
    const { id: assetId, seriesId } = this.data;

    const recordRepository = container.resolve<RecordRepository>('recordRepository');

    return recordRepository.removeRecord({ assetId, seriesId }, type);
  }

  public get recordBookmark(): KuxBookmark | undefined {
    return this.record?.bookmark;
  }

  @computed public get programState(): ProgramStateV2 {
    if (this.data.endDate < Program.now) {
      return ProgramStateV2.PAST;
    }

    if (this.data.startDate > Program.now) {
      return ProgramStateV2.FUTURE;
    }

    return ProgramStateV2.LIVE;
  }

  @computed public get parentalRating(): number {
    if (!this.data.isNotFound) {
      this.checkLinear();
    }
    return !this.data.isNotFound ? this.data.parentalRating : this.linear?.data.parentalRating || 0;
  }

  @computed public get parentalRatingString(): string | undefined {
    return !this.data.isNotFound ? getParentalRatingString(this.data) : getParentalRatingString(this.linear?.data);
  }

  /**
   * Sets now value and recalculates dependencies
   */
  @action.bound
  static setNow(now: number = Date.now()) {
    Program.now = now;
  }

  private checkLinear() {
    if (!this.linear) {
      console.log(`mediaFileId - setup linear to program. ProgramId - ${this.id}`);
    }
  }

  get isGroupedSeries(): boolean {
    return this.data.seriesId ? this.data.isGroupedSeries : false;
  }
  get isTypeVideo(): boolean {
    return this.data.mosaicInfo?.type === 'video';
  }
}

setInterval(() => Program.setNow(), 1000);
